export class DateUtils {
  /**
   * Parse an API date string (ISO or YYYY-MM-DD) into a local Date at midnight.
   * Use this when displaying dates from the API to avoid timezone shifting the day
   * (e.g. "2009-03-04T08:00:00Z" should display as March 4, not March 3).
   */
  static parseDateOnly(value: string | null | undefined): Date | null {
    if (value == null || value === '') return null;
    const s = String(value).trim();
    // Do not parse ISO datetimes here — the UTC date prefix can be one day behind local calendar dates.
    if (s.includes('T')) return null;
    const match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (!match) return null;
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const d = new Date(year, month, day);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * Parse MM/DD/YYYY (or M/D/YYYY) as a local calendar date.
   */
  static parseUsDateString(value: string | null | undefined): Date | null {
    if (value == null || value === '') return null;
    const s = String(value).trim();
    const match = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return null;
    const month = parseInt(match[1], 10) - 1;
    const day = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    const d = new Date(year, month, day);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * Parse date-only values from Date, YYYY-MM-DD, or MM/DD/YYYY without UTC day shift.
   */
  static coerceToLocalDate(value: Date | string | number | null | undefined): Date | null {
    if (value == null || value === '') return null;
    if (value instanceof Date) {
      if (isNaN(value.getTime())) return null;
      const iso = value.toISOString();
      const isUtcMidnight = /T00:00:00\.000Z$/.test(iso);
      const notLocalMidnight =
        value.getHours() !== 0 ||
        value.getMinutes() !== 0 ||
        value.getSeconds() !== 0 ||
        value.getMilliseconds() !== 0;
      // `new Date('YYYY-MM-DD')` is UTC midnight and shifts to the previous calendar day in US zones.
      if (isUtcMidnight && notLocalMidnight) {
        const fromPart = DateUtils.parseDateOnly(iso.slice(0, 10));
        if (fromPart) return fromPart;
      }
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.includes('T')) {
        // API date-only at UTC midnight: use YYYY-MM-DD prefix (not local getDate).
        if (/T00:00:00(?:\.000)?Z$/i.test(trimmed)) {
          const fromPart = DateUtils.parseDateOnly(trimmed.split('T')[0]);
          if (fromPart) return fromPart;
        }
        // JSON.stringify(local Date) — use the calendar day in the user's timezone.
        const parsed = new Date(trimmed);
        if (!isNaN(parsed.getTime())) {
          return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
        }
        return null;
      }
      const iso = DateUtils.parseDateOnly(trimmed);
      if (iso) return iso;
      const us = DateUtils.parseUsDateString(trimmed);
      if (us) return us;
      return null;
    }
    if (typeof value === 'number') {
      const parsed = new Date(value);
      if (isNaN(parsed.getTime())) return null;
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
    return null;
  }

  /**
   * Angular DatePipe treats DD as day-of-week; configs use MM/DD/YYYY — normalize to MM/dd/yyyy.
   */
  static normalizeAngularDateFormat(format: string): string {
    if (!format) return 'MM/dd/yyyy';
    return format.replace(/YYYY/g, 'yyyy').replace(/DD/g, 'dd');
  }

  /**
   * Format as MM/DD/YYYY using local calendar date parts (no timezone conversion).
   */
  static formatUsDisplay(date: Date | string | number | null | undefined): string {
    const d = DateUtils.coerceToLocalDate(date);
    if (!d) return '';
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }

  /**
   * Format a Date as YYYY-MM-DD using local date parts (no timezone conversion).
   * Use this when sending dates to the API so the stored date is not shifted.
   */
  static formatDate(date: Date | string | number | null | undefined, format: string = 'YYYY-MM-DD'): string {
    const d = DateUtils.coerceToLocalDate(date);
    if (!d) return '';

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    if (format === 'MM/DD/YYYY' || format === 'MM/dd/yyyy') {
      return `${month}/${day}/${year}`;
    }

    return format
      .replace('YYYY', String(year))
      .replace('MM', month)
      .replace('DD', day);
  }

  /** API payload date string (YYYY-MM-DD, local calendar). */
  static toApiDateString(date: Date | string | number | null | undefined): string {
    return DateUtils.formatDate(date, 'YYYY-MM-DD');
  }

  static isToday(date: Date | string): boolean {
    const d = DateUtils.coerceToLocalDate(date);
    if (!d) return false;
    const today = new Date();
    return d.toDateString() === today.toDateString();
  }

  static addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }
}
